import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
  FormHelperText,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/UserSlice";
import { loginUser } from "../../requests/auth/Authentication";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useState } from "react";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Mail, Phone } from "lucide-react";
import {setToken} from "../../utils/token"

const validationSchema = Yup.object().shape({
  emailOrPhone: Yup.string()
    .required("This field is required"),
  password: Yup.string()
    .required("Password is required"),
});

const initialValues = {
  emailOrPhone: "",
  password: "",
};

function Signin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const loginUserMutation = useMutation(loginUser, {
    onSuccess: (response) => {
      setIsLoading(false);
      console.log(response.data);
      if(response.data.in_error){
        toast.error(response?.data?.reason);
        return;
      }
      if (response?.data?.data) {
        const userData = response.data.data;      
        dispatch(setUser(userData));
        if (userData.token) {
          setToken(userData.token);
        }
        if (!userData.email_verified_at && !userData.phone_number_verified_at) {
          navigate('/verify-otp');
          return;
        } else {
          toast.success(response.data.reason || "Login successful!");
          navigate("/dashboard");
        }
      }
    },
    onError: (error) => {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.message || 
        "Invalid credentials. Please try again."
      );
    },
  });

  const handleSubmit = (values) => {
    setIsLoading(true);
    loginUserMutation.mutate({
      emailOrPhone: values.emailOrPhone,
      password: values.password
    });
  };

  const handleTabChange = (e, newValue, setFieldValue) => {
    setActiveTab(newValue);
    
    if (newValue === 0) {
      const phoneVal = phoneValue || "233";
      setPhoneValue(phoneVal.startsWith('233') ? phoneVal : '233' + phoneVal.replace(/^233/, ''));
      setFieldValue('emailOrPhone', phoneVal.startsWith('233') ? phoneVal : '233' + phoneVal.replace(/^233/, ''));
      setEmailValue("");
    } else {
      setFieldValue('emailOrPhone', emailValue);
      setPhoneValue("233");
    }
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute w-[600px] h-[600px] bg-white/5 rounded-full blur-lg"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">2000+ Active Users</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Trusted by 500+ Companies</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-20 top-32 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">4.9★ Average Rating</span>
          </div>
        </motion.div>

        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_seven}
              alt="dealboxx-signup"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />
            
            <motion.div 
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div
                className="px-8 text-center"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                <motion.h2 
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Welcome Back
                </motion.h2>
                <motion.p 
                  className="mx-auto mb-8 max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Your journey to success continues here
                </motion.p>
                
                <motion.div 
                  className="flex gap-6 justify-center text-white/90"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.6 }}
                >
                  <div className="text-center">
                    <div className="mb-1 text-3xl font-bold">24/7</div>
                    <div className="text-sm">Support</div>
                  </div>
                  <div className="text-center">
                    <div className="mb-1 text-3xl font-bold">100%</div>
                    <div className="text-sm">Secure</div>
                  </div>
                  <div className="text-center">
                    <div className="mb-1 text-3xl font-bold">Fast</div>
                    <div className="text-sm">Processing</div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute right-20 top-1/4 z-20 w-20 h-20 rounded-full border-4 border-white/20"
          animate={{ 
            y: [0, -20, 0],
            rotate: 360 
          }}
          transition={{ 
            y: { duration: 2, repeat: Infinity },
            rotate: { duration: 8, repeat: Infinity }
          }}
        />
        <motion.div
          className="absolute left-20 bottom-1/4 z-20 w-12 h-12 rounded-full border-4 border-white/20"
          animate={{ 
            y: [0, 20, 0],
            rotate: -360 
          }}
          transition={{ 
            y: { duration: 2, repeat: Infinity },
            rotate: { duration: 8, repeat: Infinity }
          }}
        />
      </motion.div>

      <motion.div 
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <Link to="/" className="flex items-center gap-2">
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
          </Link>
        </motion.div>
        <motion.div
          className="mb-6 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Welcome Back
          </h1>
          <p className="text-[#808080] text-base">
            Thrilled to Have You back With Us.
          </p>
        </motion.div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, touched, errors, setFieldValue, values }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form onSubmit={handleSubmit} className="space-y-4">
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => handleTabChange(e, newValue, setFieldValue)}
                  variant="fullWidth"
                  sx={{
                    mb: 3,
                    '& .MuiTab-root': {
                      color: '#808080',
                      textTransform: 'none',
                      fontSize: '15px',
                      minHeight: '56px',
                      '&.Mui-selected': {
                        color: '#FF6C6C',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 108, 108, 0.04)',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#FF6C6C',
                      height: '3px',
                      borderRadius: '3px',
                    },
                  }}
                >
                  <Tab 
                    icon={<Phone size={18} />} 
                    iconPosition="start" 
                    label="Phone Number"
                    sx={{
                      gap: '8px',
                      '& .MuiTab-iconWrapper': {
                        marginBottom: '0 !important',
                      }
                    }}
                  />
                  <Tab 
                    icon={<Mail size={18} />} 
                    iconPosition="start" 
                    label="Email"
                    sx={{
                      gap: '8px',
                      '& .MuiTab-iconWrapper': {
                        marginBottom: '0 !important',
                      }
                    }}
                  />
                </Tabs>

                <motion.div className="space-y-4">
                  {/* Login Field */}
                  {activeTab === 0 ? (
                    <Field name="emailOrPhone">
                      {({ field, form }) => (
                        <PhoneInput
                          country={'gh'}
                          onlyCountries={['gh']}
                          disableDropdown={true}
                          countryCodeEditable={false}
                          enableSearch={false}
                          value={phoneValue || "233"}
                          inputProps={{
                            name: 'emailOrPhone',
                            required: true,
                          }}
                          inputStyle={{
                            width: '100%',
                            height: '56px',
                            borderRadius: '12px',
                            fontSize: '16px',
                            backgroundColor: 'white',
                            paddingLeft: '48px',
                          }}
                          buttonStyle={{
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'default',
                          }}
                          containerStyle={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                          }}
                          onChange={(value) => {
                            if (!value.startsWith('233')) {
                              value = '233' + value.replace(/^233/, '');
                            }
                            setPhoneValue(value);
                            form.setFieldValue('emailOrPhone', value);
                          }}
                          isValid={(value) => {
                            return value.startsWith('233');
                          }}
                        />
                      )}
                    </Field>
                  ) : (
                    <Field
                      name="emailOrPhone"
                      as={TextField}
                      label="Email"
                      type="email"
                      variant="outlined"
                      fullWidth
                      value={emailValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmailValue(value);
                        setFieldValue('emailOrPhone', value);
                      }}
                      error={Boolean(errors.emailOrPhone && touched.emailOrPhone)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: 'white',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FF6C6C',
                            }
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FF6C6C'
                        }
                      }}
                    />
                  )}
                  <ErrorMessage
                    name="emailOrPhone"
                    component={FormHelperText}
                    className="ml-3 text-sm italic text-red-500"
                  />

                  {/* Password Field */}
                  <div className="relative">
                    <Field
                      name="password"
                      as={TextField}
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.password && touched.password)}
                      helperText={
                        <ErrorMessage
                          name="password"
                          component={FormHelperText}
                          className="text-sm italic text-red-500"
                        />
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              className="text-gray-500 hover:text-[#FF6C6C]"
                            >
                              {showPassword ? (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: 'white',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FF6C6C',
                            }
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FF6C6C'
                        }
                      }}
                    />
                  </div>

                  {/* Forgot Password Link */}
                  <motion.div 
                    className="flex justify-end"
                    whileHover={{ scale: 1.02 }}
                  >
                    <Link
                      to="/forgot-password"
                      state={{ loginType: activeTab === 0 ? 'phone' : 'email' }}
                      className="text-[#FF6C6C] font-semibold hover:text-[#ff8b8b] transition-colors duration-300"
                    >
                      Forgot Password?
                    </Link>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative ${
                      isLoading ? 'opacity-90' : 'hover:scale-102'
                    }`}
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                  >
                    {isLoading ? (
                      <div className="flex gap-2 justify-center items-center">
                        <CircularProgress 
                          size={20} 
                          thickness={5}
                          sx={{ 
                            color: 'white',
                            marginRight: '8px'
                          }} 
                        />
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      "Sign In"
                    )}
                  </motion.button>
                </motion.div>
              </Form>
            </motion.div>
          )}
        </Formik>

        {/* Sign Up Link */}
        <motion.div
          className="mt-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          <p className="text-[#808080]">
            Don't have an account?{' '}
            <motion.span whileHover={{ scale: 1.05 }}>
              <Link
                to="/signup"
                className="text-[#FF6C6C] font-semibold hover:text-[#ff8b8b] transition-colors duration-300"
              >
                Sign Up
              </Link>
            </motion.span>
          </p>
        </motion.div>

        {/* Additional Features */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Secure Login</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Access</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🛡️</div>
              <div className="text-xs text-gray-600">Data Protection</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Signin;